import { useCallback, useEffect, useRef, type RefObject } from 'react';

const SCROLLING_CLASS = 'is-scrolling';

/**
 * While `el` is scrolling (or receiving wheel), toggles `.is-scrolling` so CSS can
 * clear sticky :hover / pause shine — desktop fast scroll otherwise paints lifts over neighbors.
 */
export function useScrollingClass(
  ref: RefObject<HTMLElement | null>,
  idleMs = 220
): void {
  const idleMsRef = useRef(idleMs);
  idleMsRef.current = idleMs;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let timer = 0;
    const arm = () => {
      if (!el.classList.contains(SCROLLING_CLASS)) {
        el.classList.add(SCROLLING_CLASS);
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        el.classList.remove(SCROLLING_CLASS);
      }, idleMsRef.current);
    };

    el.addEventListener('scroll', arm, { passive: true });
    // Capture: wheel targets the tile under the cursor; fire before bubble quirks.
    el.addEventListener('wheel', arm, { passive: true, capture: true });
    el.addEventListener('touchmove', arm, { passive: true, capture: true });

    return () => {
      el.removeEventListener('scroll', arm);
      el.removeEventListener('wheel', arm, { capture: true } as EventListenerOptions);
      el.removeEventListener('touchmove', arm, { capture: true } as EventListenerOptions);
      window.clearTimeout(timer);
      el.classList.remove(SCROLLING_CLASS);
    };
  }, [ref]);
}

/** Callback-ref variant when the scrollport mounts after first paint. */
export function useScrollingClassCallback(idleMs = 220): (node: HTMLElement | null) => void {
  const idleMsRef = useRef(idleMs);
  idleMsRef.current = idleMs;
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;

    let timer = 0;
    const arm = () => {
      if (!node.classList.contains(SCROLLING_CLASS)) {
        node.classList.add(SCROLLING_CLASS);
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        node.classList.remove(SCROLLING_CLASS);
      }, idleMsRef.current);
    };

    node.addEventListener('scroll', arm, { passive: true });
    node.addEventListener('wheel', arm, { passive: true, capture: true });
    node.addEventListener('touchmove', arm, { passive: true, capture: true });
    cleanupRef.current = () => {
      node.removeEventListener('scroll', arm);
      node.removeEventListener('wheel', arm, { capture: true } as EventListenerOptions);
      node.removeEventListener('touchmove', arm, { capture: true } as EventListenerOptions);
      window.clearTimeout(timer);
      node.classList.remove(SCROLLING_CLASS);
    };
  }, []);
}
